/**
 * A FOLGA do enquadramento dos mapas de resultados (`fitBounds`): o que o pino
 * cresce para fora do ponto MAIS o que o chamador desenha por cima do mapa.
 * Nasceu no mapa do hotel e serve ao do carro (#3650) — é geometria pura, sem
 * Mapbox, sem React.
 */

/** Quantos pixels de cromo o CHAMADOR desenha por cima do mapa. Só entra no
 *  enquadramento: é o que impede o `fitBounds` de parar pinos atrás da barra de
 *  busca ou do dock de cards. Sem cromo por cima, nada a declarar. */
export type MapOverlays = {
  readonly top?: number;
  readonly bottom?: number;
  /** A COLUNA que o chamador encosta à esquerda do mapa — os filtros do mapa
   *  cheio no desktop. Sem ela os pinos da ponta oeste paravam atrás do painel
   *  (#3217 C5). */
  readonly left?: number;
  /**
   * A FOLHA que cobre a base do mapa no palco map-centric (#3106): diferente do
   * cromo flutuante, ela é um pedaço INTEIRO da tela, então entra inteira no
   * enquadramento — sem o teto que o cromo paga — e só respeita o piso de mapa
   * visível. Sem folha, nada a declarar.
   */
  readonly sheet?: number;
};

/** O que o PINO cresce para fora da coordenada, em pixels — e é folga que todo
 *  mapa paga, porque o `fitBounds` enquadra PONTOS, não caixas. A pílula sobe
 *  36px acima do ponto (corpo de 28 mais o bico) e é uma etiqueta de ~190px
 *  centrada nele, então sem meia etiqueta nas laterais o rótulo da ponta vaza e
 *  o `overflow: hidden` do mapa o corta. Abaixo do ponto não há peça nenhuma —
 *  os 12px são só respiro.
 *  Somado ao cromo do chamador, é isto que garante a pílula INTEIRA livre: o
 *  ponto para em `cromo + 36`, então o topo dela encosta exatamente na borda de
 *  baixo do cromo. */
const MAP_PIN_FIT_PADDING = { top: 36, bottom: 12, left: 112, right: 112 };

/** Nenhum lado pode gastar mais que isto de folga: no card do chat o mapa tem
 *  ~450px de altura, e a calha fixa comia metade dela — sobrava tão pouco espaço
 *  útil que o `fitBounds` devolvia um enquadramento pior que o zoom fixo que
 *  veio substituir. A folga é um pedido, não uma garantia. */
const MAP_FIT_PADDING_SHARE = 0.32;

/** O que a folha do palco map-centric nunca toma do mapa: abaixo disto os
 *  pinos se espremem numa faixa e o enquadramento deixa de dizer onde estão. */
const MAP_FIT_MIN_VIEW_PX = 160;

export type MapFitPadding = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

/**
 * A folga do enquadramento: o que o pino cresce para fora do ponto MAIS o que o
 * chamador desenha por cima deste mapa.
 *
 * A segunda parcela é do chamador porque ela não é do mapa: o palco tem a barra
 * de busca flutuando no topo e o dock de cards cobrindo a base; o mapa cheio tem
 * só a barra; o mapa do painel **não tem nada** por cima — a busca dele vive no
 * cabeçalho do painel, fora do mapa. Em constante única os três pagavam a conta
 * do palco, e num mapa de 224px de altura isso era fatal: 72 em cima e 72
 * embaixo (cada lado dentro do teto, juntos gastando 64%), sobravam 80px, o
 * `fitBounds` espremia os hotéis neles e o declutter — correto — via tudo
 * colidindo e recolhia os seis pinos em ponto (o degrau que existia então).
 * Nenhum preço no mapa.
 *
 * A folha (`overlays.sheet`) soma DEPOIS do teto: ela cobre a base de verdade,
 * e o teto de 32% deixaria os pinos exatamente atrás dela. O que a limita é o
 * piso de mapa visível — com o mapa inteiro coberto, a folga volta ao teto.
 */
export function fitPadding(
  size: { readonly clientWidth: number; readonly clientHeight: number },
  overlays: MapOverlays,
): MapFitPadding {
  const cap = (value: number, extent: number) =>
    Math.max(0, Math.min(value, Math.round(extent * MAP_FIT_PADDING_SHARE)));
  const { clientWidth: width, clientHeight: height } = size;
  const top = cap(MAP_PIN_FIT_PADDING.top + (overlays.top ?? 0), height);
  const chromeBottom = cap(MAP_PIN_FIT_PADDING.bottom + (overlays.bottom ?? 0), height);
  const sheetRoom = Math.max(0, height - top - chromeBottom - MAP_FIT_MIN_VIEW_PX);
  const sheet = Math.max(0, Math.min(overlays.sheet ?? 0, sheetRoom));
  return {
    top,
    bottom: chromeBottom + sheet,
    left: cap(MAP_PIN_FIT_PADDING.left + (overlays.left ?? 0), width),
    right: cap(MAP_PIN_FIT_PADDING.right, width),
  };
}

/** A chave escalar do enquadramento: `overlays` chega como objeto novo a cada
 *  render, e como dependência crua re-enquadraria sempre — arrancando a câmera
 *  da mão de quem está navegando. */
export function overlaysKey(overlays: MapOverlays): string {
  return [overlays.top, overlays.bottom, overlays.sheet, overlays.left]
    .map((value) => String(value ?? 0))
    .join(':');
}
